import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';

import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { FileTypeService } from '../../tracker/services/file-type.service';
import { FilePreviewService } from '../../../widgets/gf-file-preview/gf-file-preview.service';

@Component({
    selector: 'wiki-attachments',
    template: require('./wiki.attachments.component.html')
})
export class WikiAttachmentsComponent implements OnInit {
    @Input() wikiPage: any;
    @Input() project: any;

    files: any[] = [];
    selectedFile: any = null;
    selectedFileContent: string = '';
    loading: boolean = false;
    isLoadingFile: boolean = false;
    confirmingDeleteFile: boolean = false;

    constructor(
        private wikiService: WikiService,
        private uiService: UiService,
        private translateService: GfTranslateService,
        private http: HttpClient,
        private fileTypeService: FileTypeService,
        private filePreviewService: FilePreviewService
    ) {}

    get canEdit(): boolean {
        return _.get(this.project, 'rel.permissions.wiki', 0) > 1;
    }

    get deleteConfirmMessage(): string {
        return this.translateService.translate(
            'Are you sure you want to delete "{{filename}}"? This will permanently delete the file and may result in broken links on any wiki pages that reference it.',
            { filename: this.selectedFile?.fileName }
        );
    }

    ngOnInit(): void {
        this.loadAttachments();
    }

    private loadAttachments(): void {
        if (!this.wikiPage || !this.wikiPage.id) { return; }
        this.loading = true;
        this.wikiService.getAttachments(this.wikiPage.id)
            .then((response: any) => {
                this.files = (response && response.items) ? response.items : [];
                this.wikiService.attachmentCount$.next(this.files.length);
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
            });
    }

    selectFile(file: any): void {
        this.selectedFile = file;
        this.selectedFileContent = '';
        this.isLoadingFile = false;
        this.confirmingDeleteFile = false;

        if (this.isMarkdown(file)) {
            this.isLoadingFile = true;
            this.http.get(file.attachmentUrl, { responseType: 'text' })
                .toPromise()
                .then((content: string) => {
                    this.selectedFileContent = content;
                    this.isLoadingFile = false;
                })
                .catch(() => {
                    this.isLoadingFile = false;
                    this.uiService.message.showWarning(
                        this.translateService.translate('Could not load file content'),
                        this.translateService.translate('Attachments')
                    );
                });
        }
    }

    promptDeleteFile(): void {
        this.confirmingDeleteFile = true;
    }

    cancelDeleteFile(): void {
        this.confirmingDeleteFile = false;
    }

    deleteFile(file: any): void {
        this.confirmingDeleteFile = false;
        this.wikiService.deleteAttachment(this.wikiPage.id, file.id)
            .then(() => {
                this.files = this.files.filter((f: any) => f.id !== file.id);
                this.wikiService.attachmentCount$.next(this.files.length);
                if (this.selectedFile && this.selectedFile.id === file.id) {
                    this.selectedFile = null;
                    this.selectedFileContent = '';
                }
            })
            .catch(() => {
                this.uiService.message.showWarning(
                    this.translateService.translate('Could not delete file'),
                    this.translateService.translate('Attachments')
                );
            });
    }

    openImageLightbox(file: any): void {
        this.filePreviewService.openImage(file, file.attachmentUrl, { centered: true });
    }

    copyUrl(file: any): void {
        navigator.clipboard.writeText(file.attachmentUrl).catch(() => {});
    }

    copyMarkdown(file: any): void {
        const url = file.attachmentUrl;
        const name = file.fileName;
        const text = this.isImage(file) ? `![${name}](${url})` : `[${name}](${url})`;
        navigator.clipboard.writeText(text).catch(() => {});
    }

    isMarkdown(file: any): boolean { return this.fileTypeService.isMarkdown(file); }
    isHtml(file: any): boolean { return this.fileTypeService.isHtml(file); }
    isImage(file: any): boolean { return this.fileTypeService.isImage(file); }
    isVideo(file: any): boolean { return this.fileTypeService.isVideo(file); }
    isAudio(file: any): boolean { return this.fileTypeService.isAudio(file); }
    isRenderable(file: any): boolean { return this.fileTypeService.isRenderable(file); }
    formatFileSize(bytes: number): string { return this.fileTypeService.formatFileSize(bytes); }
}
